<template>
    <div class="dashboard-container">
        <!-- ສ່ວນຫົວຂອງໜ້າ -->
        <div class="page-header">
            <div class="breadcrumb">ໜ້າຫຼັກ / <span>Dashboard</span></div>
            <h1>ບົດສະຫຼຸບພາບລວມຂອງໂຮງຮຽນ</h1>
            <p>ຂໍ້ມູນສັງລວມຈຳນວນນັກຮຽນ, ຄູສອນ ແລະ ສະຖິຕິການມາຮຽນ</p>
        </div>

        <!-- ສ່ວນບັດສະຫຼຸບຕົວເລກ (KPIs) -->
        <div class="kpi-grid">
            <div class="kpi-card blue">
                <div class="kpi-label">ນັກຮຽນທັງໝົດ</div>
                <div class="kpi-value">{{ stats.totalStudents.toLocaleString() }}</div>
                <div class="kpi-badge up">
                    <i class="fas fa-venus-mars"></i> ຍິງ: {{ stats.femaleStudents }} | ຊາຍ: {{ stats.maleStudents }}
                </div>
            </div>
            <div class="kpi-card red">
                <div class="kpi-label">ຄູສອນ ແລະ ພະນັກງານ</div>
                <div class="kpi-value">{{ stats.totalTeachers.toLocaleString() }}</div>
                <div class="kpi-badge down">ພະນັກງານໃນລະບົບ</div>
            </div>
            <div class="kpi-card green">
                <div class="kpi-label">ອັດຕາສ່ວນເພດຍິງ</div>
                <div class="kpi-value">{{ femalePercentage }}%</div>
                <div class="kpi-sub">ຂອງຈຳນວນນັກຮຽນທັງໝົດ</div>
            </div>
            <div class="kpi-card gold">
                <div class="kpi-label">ສະເລ່ຍນັກຮຽນ/ຄູ</div>
                <div class="kpi-value">{{ studentTeacherRatio }}</div>
                <div class="kpi-badge warn">ອັດຕາສ່ວນຄວາມເໝາະສົມ</div>
            </div>
        </div>

        <!-- ສ່ວນສະແດງຜົນການວິເຄາະ ແລະ ລາຍການຫຼ້າສຸດ -->
        <div class="two-col">
            <!-- ກຣາຟຈຳລອງ (Visualization Placeholder) -->
            <div class="panel">
                <div class="panel-head">
                    <h3><i class="fas fa-chart-pie"></i> ສັດສ່ວນນັກຮຽນແຍກຕາມເພດ</h3>
                </div>
                <div class="panel-body">
                    <div class="chart-container">
                        <div class="fake-chart">
                            <div class="bar-y-axis">
                                <span>{{ stats.totalStudents }}</span><span>{{ Math.round(stats.totalStudents/2) }}</span><span>0</span>
                            </div>
                            <div class="bar-area">
                                <div class="bar-group" title="ນັກຮຽນຍິງ">
                                    <div class="bar female-bar" :style="{ height: (stats.femaleStudents / stats.totalStudents * 100) + '%' }"></div>
                                    <span class="bar-label">ຍິງ</span>
                                </div>
                                <div class="bar-group" title="ນັກຮຽນຊາຍ">
                                    <div class="bar male-bar" :style="{ height: (stats.maleStudents / stats.totalStudents * 100) + '%' }"></div>
                                    <span class="bar-label">ຊາຍ</span>
                                </div>
                            </div>
                        </div>
                        <div class="chart-legend">
                            <span class="legend-item"><span class="dot female-dot"></span> ນັກຮຽນຍິງ</span>
                            <span class="legend-item"><span class="dot male-dot"></span> ນັກຮຽນຊາຍ</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ລາຍການກິດຈະກຳຫຼ້າສຸດ -->
            <div class="panel">
                <div class="panel-head">
                    <h3>ລາຍການເຄື່ອນໄຫວຫຼ້າສຸດ</h3>
                    <span class="panel-action">ເບິ່ງທັງໝົດ</span>
                </div>
                <div class="panel-body">
                    <div class="activity-list">
                        <div v-for="(item, index) in recentStudents" :key="index" class="activity-item">
                            <div class="act-icon income">
                                👤
                            </div>
                            <div class="act-text">
                                <strong>ລົງທະບຽນນັກຮຽນໃໝ່</strong>
                                <p>{{ item.firstNameLao }} {{ item.familyNameLao }}</p>
                            </div>
                            <div class="act-amount income" style="font-size: 11px;">
                                ຊັ້ນ {{ item.grade }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/utils/api';

export default {
    name: 'DashboardPage',
    data() {
        return {
            loading: false,
            stats: {
                totalStudents: 0,
                totalTeachers: 0,
                maleStudents: 0,
                femaleStudents: 0
            },
            recentStudents: [],
            user: JSON.parse(localStorage.getItem('user-info') || '{}')
        }
    },
    computed: {
        femalePercentage() {
            if (this.stats.totalStudents === 0) return 0;
            return Math.round((this.stats.femaleStudents / this.stats.totalStudents) * 100);
        },
        studentTeacherRatio() {
            if (this.stats.totalTeachers === 0) return 'N/A';
            return Math.round(this.stats.totalStudents / this.stats.totalTeachers) + ':1';
        }
    },
    mounted() {
        this.fetchDashboardData();
    },
    methods: {
        async fetchDashboardData() {
            this.loading = true;
            try {
                const res = await api.get('/dashboard/stats', {
                    params: { school_id: this.user.school_id }
                });
                if (res.data.code === 1) {
                    this.stats = res.data.stats;
                    this.recentStudents = res.data.recent;
                }
            } catch (e) {
                console.error('Dashboard Error:', e);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.dashboard-container {
    animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

/* Chart Placeholder Styles */
.chart-container {
    padding: 10px 0;
}

.fake-chart {
    display: flex;
    height: 200px;
    gap: 20px;
    border-bottom: 2px solid var(--border);
    padding-bottom: 10px;
    position: relative;
}

.bar-y-axis {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    color: var(--text3);
    font-size: 11px;
    width: 40px;
}

.bar-area {
    flex: 1;
    display: flex;
    justify-content: space-around;
    align-items: flex-end;
}

.bar-group {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    width: 60px;
    position: relative;
}

.bar {
    flex: 1;
    border-radius: 4px 4px 0 0;
    min-height: 10px;
    transition: transform 0.3s ease;
}

.bar:hover { transform: scaleY(1.05); }
.bar.female-bar { background: #ec4899; } /* Pink for female */
.bar.male-bar { background: #3b82f6; }   /* Blue for male */
.female-dot { background: #ec4899; }
.male-dot { background: #3b82f6; }

.bar-label {
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: var(--text2);
    white-space: nowrap;
}

.chart-legend {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 35px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text2);
}

.dot { width: 12px; height: 12px; border-radius: 3px; }
.dot.income { background: var(--success); }
.dot.expense { background: var(--danger); }

.act-amount {
    font-weight: 600;
    font-size: 13px;
}

.act-amount.income { color: var(--success); }
.act-amount.expense { color: var(--danger); }

.act-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
}
.act-icon.income { background: var(--success-bg); color: var(--success); }
.act-icon.expense { background: var(--danger-bg); color: var(--danger); }

/* Alert Styles */
.alert-list { display: flex; flex-direction: column; gap: 12px; }
.alert-item { 
    display: flex; align-items: center; padding: 16px 20px; 
    border-radius: 12px; border: 1px solid; transition: 0.3s;
    cursor: pointer;
}
.alert-item:hover { transform: translateX(5px); }
.alert-item.warn { background: #FFFBEB; border-color: #FEF3C7; color: #92400E; }
.alert-item.danger { background: #FEF2F2; border-color: #FEE2E2; color: #991B1B; }

.alert-icon { font-size: 20px; margin-right: 15px; }
.alert-text { flex: 1; }
.alert-text strong { display: block; font-size: 14px; margin-bottom: 2px; }
.alert-text p { font-size: 12px; opacity: 0.8; margin: 0; }
.alert-link { font-size: 12px; opacity: 0.3; }

.mb-4 { margin-bottom: 1.5rem; }
</style>
 